
import { UseFormRegister, FieldErrors, UseFormWatch } from "react-hook-form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";

interface AddressSectionProps {
  register: UseFormRegister<any>;
  errors: FieldErrors;
  setValue: (name: string, value: any) => void;
  watch: UseFormWatch<any>;
}

interface Canton {
  code: string;
  name_de: string;
}

export function AddressSection({ register, errors, setValue, watch }: AddressSectionProps) {
  const [cantons, setCantons] = useState<Canton[]>([]);

  useEffect(() => {
    const fetchCantons = async () => {
      const { data } = await supabase
        .from('swiss_cantons')
        .select('code, name_de')
        .order('name_de');
      
      if (data) setCantons(data);
    };
    
    fetchCantons();
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Adressinformationen</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label>Strasse und Hausnummer</Label>
          <Input 
            {...register("street_address", { required: "Strasse ist erforderlich" })} 
            placeholder="Musterstrasse 123"
          />
          {errors.street_address && <p className="text-red-500 text-sm">{errors.street_address.message as string}</p>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>PLZ</Label>
            <Input 
              {...register("postal_code", { 
                required: "PLZ ist erforderlich",
                pattern: {
                  value: /^\d{4}$/,
                  message: "PLZ muss 4 Ziffern haben"
                }
              })} 
              placeholder="8001"
            />
            {errors.postal_code && <p className="text-red-500 text-sm">{errors.postal_code.message as string}</p>}
          </div>
          
          <div className="space-y-2">
            <Label>Ort</Label>
            <Input 
              {...register("city", { required: "Ort ist erforderlich" })} 
              placeholder="Zürich"
            />
            {errors.city && <p className="text-red-500 text-sm">{errors.city.message as string}</p>}
          </div>
          
          <div className="space-y-2">
            <Label>Land</Label>
            <Select onValueChange={(value) => setValue("country", value)} defaultValue="Switzerland">
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Switzerland">Schweiz</SelectItem>
                <SelectItem value="Germany">Deutschland</SelectItem>
                <SelectItem value="Austria">Österreich</SelectItem>
                <SelectItem value="France">Frankreich</SelectItem>
                <SelectItem value="Italy">Italien</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Steuerkanton</Label>
            <Select onValueChange={(value) => setValue("tax_canton", value)}>
              <SelectTrigger>
                <SelectValue placeholder="Kanton wählen..." />
              </SelectTrigger>
              <SelectContent>
                {cantons.map((canton) => (
                  <SelectItem key={canton.code} value={canton.code}>
                    {canton.name_de}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          <div className="space-y-2">
            <Label>Steuergemeinde</Label>
            <Input 
              {...register("tax_municipality")} 
              placeholder="Gemeinde"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
